import React from 'react';

import {Bucket} from 'common/widgets/bucket-pane/types';
import {ButtonReact} from 'common/components/button';
import {useTranslation} from 'common/i18n/hooks';
import {ButtonTheme} from 'common/components/button/constants';
import {ENTER_KEY_CODE} from 'common/util/dom';
import {withStyles} from 'common/util/react';
import {Segmentation} from 'common/types/reports/query';
import {PropertyType} from 'common/report/constants';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {useStagedState} from 'common/hooks/useStagedState';

// eslint-disable-next-line import/no-cycle
import {StringBucketPaneReact} from '../string-bucket-pane';
import {NumericBucketPaneReact} from '../numeric-bucket-pane';
import {DateBucketPaneReact, DateBucketPaneTimeGroup} from '../date-bucket-pane';
import styles from './index.module.styl';
import css from './index.module.styl?inline';

export type SegmentBucketPaneProps = {
  eventNames?: string[];
  header?: string;
  maxNumBuckets?: number;
  onClose?: () => void;
  onUpdateSegment: (segment: Segmentation | null) => void;
  rangeMin?: number;
  resetBucket?: boolean;
  segment: Segmentation;
  selectedBucket?: Bucket;
  selectedUnit?: string;
  units?: string[] | Array<{value: string; label: string}>;
  unitsAsProperty?: boolean;
};

export const SegmentBucketPane = withStyles((props: SegmentBucketPaneProps) => {
  const [editedBucket, setEditedBucket] = React.useState<Bucket | null>(null);
  const [segment, setSegment] = useStagedState<Segmentation | null>(props.segment, (updatedSegment) => {
    props.onUpdateSegment(updatedSegment);
  });

  const [validBucket, setValidBucket] = React.useState(true);

  const submitButton = React.useRef<any>(null);

  const {t} = useTranslation();

  const submitDisabled = !validBucket;

  const propertyType = segment?.listItemGroup?.propertyType || segment?.propertyType;

  const applyBucketSelections = () => {
    const customBucket =
      !segment ||
      segment.propertyType === PropertyType.Number ||
      segment.listItemGroup?.propertyType === PropertyType.Number
        ? editedBucket ?? props.selectedBucket
        : undefined;

    // TODO: The `any` below here is because the way this component was set up
    // is a bit odd in that we're jamming stuff on the (nullable) segment within
    // the bucket component instead of just emitting what's updated (bucket and
    // unit) and then letting the parent control the updates. This should be
    // refactored.
    setSegment(
      {
        ...segment,
        customBucket,
      } as any,
      true,
    );
  };

  const handleKeyUp = (ev: TODO) => {
    if (ev.keyCode === ENTER_KEY_CODE && !submitDisabled) {
      applyBucketSelections();
    }
  };

  const handleSegmentUpdate = ({
    focusSubmit,
    isValid,
    updatedSegment,
  }: {
    focusSubmit: boolean;
    isValid: boolean;
    updatedSegment: Segmentation;
  }) => {
    focusSubmit && submitButton.current?.focus?.();
    if (segment?.listItemGroup) {
      // For list of objects, the segment passed to `StringBucketPaneEl` is segment.listItemGroup, so
      // we use the emitted segment to update segment.listItemGroup
      const {customGroups, ...listItemGroup} = updatedSegment;
      updatedSegment = {...segment, listItemGroup, customGroups};
    }
    setSegment(updatedSegment);
    setValidBucket(isValid);
  };

  const resetInterval = () => {
    if (segment) {
      setSegment(
        {
          ...segment,
          customBucket: undefined,
          customGroups: undefined,
        },
        true,
      );
    } else {
      setSegment(null, true);
    }
  };

  const getPropertyUnit = () => {
    const unit = segment?.unit;
    if (unit === TimeUnit.Minute || unit === TimeUnit.Session) {
      throw new Error(`${unit} time buckets are not allowed`);
    }
    return unit as DateBucketPaneTimeGroup;
  };

  const handleBucketUpdate = ({bucket, isValid}: {bucket: Bucket; isValid: boolean}) => {
    setEditedBucket(bucket);
    setValidBucket(isValid);
  };

  const handleUnitUpdate = ({
    focusSubmit,
    isValid,
    unit,
  }: {
    focusSubmit: boolean;
    isValid: boolean;
    unit: DateBucketPaneTimeGroup;
  }) => {
    focusSubmit && submitButton.current?.focus?.();
    if (segment) {
      setSegment({
        ...segment,
        unit: unit as Segmentation[`unit`],
      });
    }
    setValidBucket(isValid);
  };

  const segmentParams = (() => {
    let params: {segment: Segmentation | null; listProperty?: Segmentation} = {segment};

    if (segment?.listItemGroup) {
      // For list of objects, we pass segment.listItemGroup as the segment (and include the outer customGroups).
      // listProperty is also needed so the appropriate values query can be made using the listProperty
      params = {
        segment: {
          ...segment.listItemGroup,
          ...(segment.customGroups ? {customGroups: segment.customGroups} : {}),
        },
        listProperty: segment,
      };
    }

    return params;
  })();

  return (
    <div className={styles.newStyle}>
      <div className={styles.mpBucketPane}>
        <div className={styles.inputSection} onKeyUp={handleKeyUp}>
          {propertyType === PropertyType.String || propertyType === PropertyType.List ? (
            segmentParams.segment && (
              <StringBucketPaneReact
                className={styles.inputPane}
                eventNames={props.eventNames || []}
                onUpdate={({focusSubmit, isValid, segment: updatedSegment}) =>
                  handleSegmentUpdate({
                    focusSubmit,
                    isValid,
                    updatedSegment,
                  })
                }
                segment={segmentParams.segment}
                listProperty={segmentParams.listProperty}
              />
            )
          ) : propertyType === PropertyType.Number ? (
            <NumericBucketPaneReact
              className={styles.inputPane}
              units={props.units}
              useNewDesign
              selectedBucket={editedBucket || props.selectedBucket}
              selectedUnit={props.selectedUnit}
              maxNumBuckets={props.maxNumBuckets}
              rangeMin={props.rangeMin}
              header={props.header}
              unitsAsProperty={props.unitsAsProperty}
              onUpdate={handleBucketUpdate}
            />
          ) : (
            propertyType === PropertyType.Datetime && (
              <DateBucketPaneReact className={styles.inputPane} onUpdate={handleUnitUpdate} unit={getPropertyUnit()} />
            )
          )}
        </div>
        <div className={styles.footer}>
          {props.resetBucket && (
            <ButtonReact
              className={styles.resetButton}
              elref='resetButton'
              theme={ButtonTheme.Tertiary}
              onClick={resetInterval}
            >
              {t(`common.reset`)}
            </ButtonReact>
          )}
          <div className={styles.coreBtnGroup}>
            <ButtonReact theme={ButtonTheme.Tertiary} elref='cancelButton' onClick={() => props.onClose?.()}>
              {t(`common.cancel`)}
            </ButtonReact>
            <ButtonReact
              className={styles.applyButton}
              disabled={submitDisabled}
              theme={ButtonTheme.Primary}
              elref='applyButton'
              onClick={applyBucketSelections}
              ref={submitButton}
            >
              {t(`common.apply`)}
            </ButtonReact>
          </div>
        </div>
      </div>
    </div>
  );
}, css);
